"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useState, useEffect, Children, cloneElement } from 'react';
import { HelperText } from '../../form-elements/helper-text/index.js';
import { StatusText } from '../../form-elements/status-text/index.js';
import { Label } from '../../form-elements/label/index.js';
import getOS from '../../utilities/check-os/index.js';
import '../../utilities/use-viewport/index.js';
import { validateSurface } from '../../globals.js';
import styles from './dropdown-select.module.scss.js';
import { Text } from '../../typography/text/index.js';
import DownCaret from '../../icons/component-icons/down-caret/index.js';
import Error from '../../icons/component-icons/error/index.js';

/**
 * @component DropdownSelect
 * @package Selects
 * @document ../_docs/code/DropdownSelect.editor.tsx
 * @document ../_docs/code/DropdownSelect.snippet.tsx
 * @document ../_docs/code/DropdownSelect.import.tsx
 */
const DropdownSelect = forwardRef((_a, ref) => {
    var { helperTextPosition = 'bottom', surface: surfaceProp = 'lightPrimary', inlineLabel = false, disabled = false, containerBackground = 'transparent', readOnly = false, required = true, width, id, helperText, errorText: errorTextProp, ariaLabel, label, error: errorProp, selectId: selectIdProp, onChange, onBlur, children, tooltip, 'data-testid': testId = 'select-dropdown' } = _a, rest = __rest(_a, ["helperTextPosition", "surface", "inlineLabel", "disabled", "containerBackground", "readOnly", "required", "width", "id", "helperText", "errorText", "ariaLabel", "label", "error", "selectId", "onChange", "onBlur", "children", "tooltip", 'data-testid']);
    const surface = validateSurface(surfaceProp);
    const containerStyles = [styles.container];
    const iconInlineStyles = [
        styles.iconInline,
        styles[surface.toLocaleLowerCase()],
    ];
    const innerContainerStyles = [
        styles.innerContainer,
        styles[surface.toLocaleLowerCase()],
    ];
    const colStyles = [styles.col, styles[surface.toLocaleLowerCase()]];
    const dropdownSelectStyles = [
        styles.dropdownSelect,
        styles[surface.toLocaleLowerCase()],
    ];
    const selectContainerStyles = [
        styles.selectContainer,
        styles[surface.toLocaleLowerCase()],
    ];
    const inlineLabelStyles = [
        styles.inlineLabel,
        styles[surface.toLocaleLowerCase()],
    ];
    const selectStyles = [styles.select, styles[surface.toLocaleLowerCase()]];
    const iconContainerStyles = [
        styles.iconContainer,
        styles[surface.toLocaleLowerCase()],
    ];
    const selectId = useRef(null);
    const _useId = useId();
    if (selectId.current === null)
        selectId.current = selectIdProp || _useId;
    const isAndroidDevice = getOS() === 'android';
    const [selectedValue, setSelectedValue] = useState();
    const [error, setError] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [keyboardFocused, setKeyboardFocused] = useState();
    if (error) {
        dropdownSelectStyles.push(styles.error);
        iconContainerStyles.push(styles.error);
        selectStyles.push(styles.error);
        inlineLabelStyles.push(styles.error);
    }
    if (containerBackground !== 'transparent')
        dropdownSelectStyles.push(styles.whiteBackground);
    if (disabled) {
        dropdownSelectStyles.push(styles.disabled);
        inlineLabelStyles.push(styles.disabled);
        selectStyles.push(styles.disabled);
        iconContainerStyles.push(styles.disabled);
        iconInlineStyles.push(styles.disabled);
    }
    if (readOnly) {
        dropdownSelectStyles.push(styles.readOnly);
        selectStyles.push(styles.readOnly);
    }
    if (error && !focused) {
        dropdownSelectStyles.push(styles.focusBorder);
    }
    if (keyboardFocused) {
        dropdownSelectStyles.push(styles.keyBoardFocused);
    }
    if (helperTextPosition === 'right') {
        containerStyles.push(styles.helperTextRight);
        innerContainerStyles.push(styles.helperTextRight);
        selectContainerStyles.push(styles.helperTextRight);
        colStyles.push(styles.helperTextRight);
    }
    if (focused) {
        dropdownSelectStyles.push(styles.focusedBorder);
    }
    if (inlineLabel) {
        containerStyles.push(styles.containerWithInlineLabel);
        selectStyles.push(styles.selectWithInlineLabel);
        iconInlineStyles.push(styles.iconInline);
        if (error) {
            iconInlineStyles.push(styles.error);
        }
    }
    if (helperText) {
        innerContainerStyles.push(styles.helpertext);
        colStyles.push(styles.helpertext);
    }
    useEffect(() => {
        if (typeof errorProp === 'boolean') {
            setError(errorProp);
        }
    }, [errorProp]);
    useEffect(() => {
        const selectEle = document &&
            selectId.current &&
            document.getElementById(selectId.current);
        /* istanbul ignore else -- @preserve */
        if (selectEle)
            setSelectedValue(selectEle.value);
    }, []);
    const trueLabel = label || '';
    const errorText = selectedValue === '' ? `You must enter a ${trueLabel}` : errorTextProp;
    const _calculateAriaLabel = () => {
        const accessLabel = ariaLabel || (!required ? `${label || ''} Optional` : `${label || ''}`);
        if (error && errorText) {
            if (helperText) {
                return `${accessLabel} ${helperText}`;
            }
            return `${accessLabel}`;
        }
        if (helperText) {
            return `${accessLabel} ${helperText}`;
        }
        return accessLabel;
    };
    const _onChange = (e) => {
        if (onChange)
            onChange(e);
        if (isAndroidDevice)
            e.target.focus();
        setSelectedValue(e.target.value);
        if ((errorProp === undefined || errorProp === false) &&
            required &&
            e.target.value) {
            setError(false);
        }
    };
    const setHover = () => {
        setHovered(true);
    };
    const unSetHover = () => {
        setHovered(false);
    };
    const _onBlur = (e) => {
        if (onBlur)
            onBlur(e);
        if (keyboardFocused) {
            setKeyboardFocused(false);
        }
        if (!e.target.value &&
            (errorProp === undefined || errorProp === false) &&
            required) {
            setError(true);
        }
        setFocused(false);
    };
    const _onFocus = () => {
        if (!hovered && !keyboardFocused)
            setKeyboardFocused(true);
        setFocused(true);
    };
    const _onMouseDown = () => {
        if (keyboardFocused)
            setKeyboardFocused(false);
    };
    const _renderChildren = () => {
        if (!children)
            return null;
        /* istanbul ignore else */
        if (children && Array.isArray(children)) {
            const isFirstItemDisabled = children[0]
                ? children[0].props && children[0].props.disabled
                : children['props'] && children['props'].disabled;
            const totalItems = isFirstItemDisabled
                ? children.length - 1
                : children.length;
            return Children.map(children, (child, index) => {
                const optionAriaLabel = index === 0 && isFirstItemDisabled
                    ? `${child.props.children}`
                    : `${child.props.children === selectedValue ? 'selected' : ''} ${child.props.children} item ${index + (isFirstItemDisabled ? 0 : 1)} of ${totalItems > 1 ? totalItems : 1}`;
                return cloneElement(child, {
                    'aria-label': isAndroidDevice
                        ? `${_calculateAriaLabel()} ${optionAriaLabel}`
                        : optionAriaLabel,
                    onMouseEnter: setHover,
                    onMouseLeave: unSetHover,
                    role: isAndroidDevice
                        ? undefined
                        : 'option' /* need role="option" to prevent double reading by NVDA */,
                });
            });
        }
        return cloneElement(
        // eslint-disable-next-line
        children, {
            onMouseEnter: setHover,
            onMouseLeave: unSetHover,
            role: isAndroidDevice
                ? undefined
                : 'option' /* need to add role="option" here to prevent double reading by screen reader */,
        });
    };
    const _renderIcon = () => {
        // Error state has preference over component's error
        if (error && !focused) {
            return (jsx("div", { className: iconContainerStyles.join(' '), tabIndex: -1, "aria-hidden": true, children: !readOnly && jsx(Error, { tabIndex: -1, size: 20, ariaHidden: true }) }));
        }
        return (jsx("div", { className: inlineLabel && !selectedValue
                ? iconInlineStyles.join(' ')
                : iconContainerStyles.join(' '), tabIndex: -1, "aria-hidden": true, children: !readOnly && jsx(DownCaret, { tabIndex: -1, size: 20, ariaHidden: true }) }));
    };
    const _renderHelperText = () => (jsx(HelperText, { helperText: helperText, helperTextPosition: helperTextPosition, disabled: disabled, surface: surface }));
    return (jsx("div", { className: containerStyles.join(' '), style: width ? { width } : undefined, id: id, onMouseDown: _onMouseDown, children: jsxs("div", { className: innerContainerStyles.join(' '), children: [label && !inlineLabel && (jsx(Label, { disabled: disabled, surface: surface, required: required, label: label, tooltip: tooltip, "data-testid": `${testId}-label`, id: `${selectId.current}-label`, "aria-label": label, htmlFor: selectId.current, helperTextPosition: helperTextPosition, helperText: helperText })), jsxs("div", { className: styles.row, children: [jsxs("div", { className: colStyles.join(' '), children: [jsx("div", { className: dropdownSelectStyles.join(' '), onMouseEnter: setHover, onMouseLeave: unSetHover, children: jsxs("div", { className: selectContainerStyles.join(' '), children: [inlineLabel && label && (jsx("label", { className: inlineLabelStyles.join(' '), tabIndex: -1, htmlFor: selectId.current, children: jsx(Text, { kind: "body", size: "large", bold: true, children: label }) })), jsx("select", Object.assign({}, rest, { className: selectStyles.join(' '), ref: ref, required: required, disabled: disabled, "aria-label": isAndroidDevice ? undefined : _calculateAriaLabel(), id: selectId.current, tabIndex: 0, onChange: _onChange, onFocus: _onFocus, onBlur: _onBlur, "data-testid": testId, children: _renderChildren() })), _renderIcon()] }) }), error && errorText && !disabled && !readOnly && (jsx("span", { "aria-hidden": !(getOS() === 'android') && selectedValue !== '', style: { marginTop: 'var(--vds-space-2x)' }, role: !(getOS() === 'android')
                                        ? /* istanbul ignore next */ 'alert'
                                        : undefined, children: jsx(StatusText, { errorText: errorText, surface: surface, error: error }) }))] }), helperText &&
                            helperTextPosition === 'right' &&
                            _renderHelperText()] }), helperText && helperTextPosition === 'bottom' && _renderHelperText()] }) }));
});

export { DropdownSelect, DropdownSelect as default };
